import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAsync } from 'react-use';
import services from 'services';
import utils from 'utils';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import selectors from 'state/selectors';
import actions from 'state/actions';

export default function WithLock<P extends object>(WrappedComponent: React.FunctionComponent<P>, programName: string) {
  return function ComponentWithLock(props: P) {
    // @ts-ignore
    const protocol = useSelector(selectors[programName].selectActiveDocumentID) as number;
    const idProgram = useSelector(selectors.app.getProgramCode) || -1;
    const idUser = useSelector(selectors.user.getIdUser) || -1;
    const activeLock = useSelector(selectors.documents.selectActiveLock);
    const [isWrappedComponteAvailable, setIsWrappedComponteAvailable] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();

    // Use refs to store values that won't change during cleanup
    const activeLockRef = useRef(activeLock);
    const protocolRef = useRef(protocol);
    const idProgramRef = useRef(idProgram);
    const idUserRef = useRef(idUser);
    const lockRef = useRef(false);
    const unlockedRef = useRef(false);

    // Update refs when values change
    useEffect(() => {
      if (protocol !== null) protocolRef.current = protocol;
    }, [protocol]);

    useEffect(() => {
      if (idProgram !== -1) idProgramRef.current = idProgram;
    }, [idProgram]);

    useEffect(() => {
      if (idUser !== -1) idUserRef.current = idUser;
    }, [idUser]);

    useEffect(() => {
      activeLockRef.current = activeLock;
    }, [activeLock]);
    useAsync(async () => {
      try {
        if (protocol == null || idUser == null || idProgram == null) {
          return;
        }

        // Unlock previous document before going to next in case 'open next document' settings is active
        if (activeLockRef.current && activeLockRef.current.protocol !== protocol) {
          try {
            await services.unlockDocument(activeLockRef.current);
          } catch (e) {
            console.error('Failed to unlock previous document', e);
          }
          dispatch(actions.documents.setActiveLock(null));
          unlockedRef.current = false;
        }

        const { data } = await services.lockDocument({ idUser, idProgram, protocol });
        if (data.locked) {
          lockRef.current = true;
          utils.app.notify(
            'warning',
            `Document with protocol
          ${data.protocol} is locked by ${data.usernameLock} using the module: ${data.programName}.
          Please, try later.`,
            7000,
          );
          history.goBack();
        } else {
          dispatch(actions.documents.setActiveLock({ idUser, idProgram, protocol }));
          setIsWrappedComponteAvailable(true);
        }
      } catch (e) {
        console.error(e);
        history.goBack();
      }
    }, [idUser, idProgram, protocol]);

    // UNLOCK (Unmount, Tab Close)
    const performUnlock = useCallback((): Promise<unknown> => {
      const protocolValue = protocolRef.current;
      const idProgramValue = idProgramRef.current;
      const idUserValue = idUserRef.current;
      const isActuallyLockedBySomeoneElse = lockRef.current;

      if (
        unlockedRef.current ||
        isActuallyLockedBySomeoneElse ||
        !activeLockRef.current ||
        protocolValue === null ||
        idUserValue === -1 ||
        idProgramValue === -1
      ) {
        return Promise.resolve();
      }

      unlockedRef.current = true;

      // The service now handles the fetch keepalive protection and JWT correctly.
      dispatch(actions.documents.unlockDocumentAction());

      return Promise.resolve();
    }, [dispatch]);

    // UNLOCK ON UNMOUNT (Manual exit from document)
    useEffect(() => {
      return () => {
        performUnlock();
      };
    }, [performUnlock]);

    // UNLOCK ON TAB CLOSE / REFRESH
    useEffect(() => {
      const handleBeforeUnload = () => {
        performUnlock();
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [performUnlock]);

    return isWrappedComponteAvailable ? <WrappedComponent {...props} /> : null;
  };
}
